var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { registerAutomotive } from '../../registryCore';
import { clamp, createAutoGroup, num, setParts, setRefresh, setState, } from '../../helpers';
import { themeFromProps } from '../../themes';
import { autoCenteredText, centerInBounds, fitFontSizeToWidth, fluidFont, isCompactBounds, resolveBounds, resolveDisplay, textYForBaseline } from '../../layout';
import { buildDialGauge, updateDialNeedle } from '../../../primitives/dialGauge';
import { buildDigitalGauge, digitalGaugeStyle, updateDigitalGauge } from '../../primitives/digitalGauge';
import { themedDial } from './shared';
registerAutomotive('speedometer', function (props, app) {
    var _a;
    var theme = themeFromProps(props);
    return themedDial(app, 'speedometer', __assign(__assign({}, props), { needleColor: (_a = props.needleColor) !== null && _a !== void 0 ? _a : theme.needleSpeed }), 240, 'int', 'needleSpeed', {
        redlineFrom: 0.82,
        tickCount: 12,
        unit: ' km/h',
    });
});
registerAutomotive('tachometer', function (props, app) {
    var _a;
    var theme = themeFromProps(props);
    return themedDial(app, 'tachometer', __assign(__assign({}, props), { needleColor: (_a = props.needleColor) !== null && _a !== void 0 ? _a : theme.needleTach }), 8000, 'rpm', 'needleTach', {
        redlineFrom: 0.75,
        tickCount: 8,
    });
});
registerAutomotive('engineTemp', function (props, app) {
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 140, 140);
    var value = num(props, 'value', 90);
    var max = num(props, 'max', 130);
    var display = resolveDisplay(props, 'analog');
    var useDigital = display === 'digital' || isCompactBounds(bounds);
    if (useDigital) {
        var group_1 = createAutoGroup(app, 'engineTemp', __assign(__assign({}, props), { width: bounds.width, height: bounds.height, display: 'digital' }), 'engineTemp');
        var style = digitalGaugeStyle(theme);
        var parts_1 = buildDigitalGauge(app, group_1, bounds, style, {
            label: 'Temp',
            value: value,
            max: max,
            unit: '°C',
            formatValue: function (v) { return String(Math.round(v)); },
            showBar: true,
            showSegments: false,
        });
        var barW_1 = bounds.innerWidth - bounds.pad;
        setParts(group_1, { valueText: parts_1.valueText });
        group_1.metadata._digitalParts = parts_1;
        setRefresh(group_1, function (v) { return updateDigitalGauge(parts_1, v, max, function (x) { return String(Math.round(x)); }, barW_1); });
        setState(group_1, { width: bounds.width, height: bounds.height, value: value, max: max, display: 'digital' });
        return group_1;
    }
    var size = Math.min(bounds.dialSize, Math.min(bounds.innerWidth, bounds.innerHeight) - 6);
    var group = createAutoGroup(app, 'engineTemp', __assign(__assign({}, props), { width: bounds.width, height: bounds.height, size: size, display: 'analog' }), 'engineTemp');
    var origin = centerInBounds(bounds, size, size);
    var inner = app.group({ x: origin.x, y: origin.y, listening: false });
    group.add(inner);
    var cx = size / 2;
    var inset = Math.max(4, Math.min(12, size * 0.1));
    var r = size / 2 - inset;
    var parts = buildDialGauge(app, inner, {
        trackColor: theme.dialStroke,
        needleColor: theme.text,
        accentColor: theme.ok,
        textColor: theme.text,
        textMuted: theme.textMuted,
        faceColor: '#0a0a0a',
        bezelColor: theme.dialStroke,
        tickColor: theme.textMuted,
    }, {
        size: size,
        value: value,
        max: max,
        title: 'TEMP',
        unit: '°C',
        formatValue: function (v) { return String(Math.round(v)); },
        tickCount: size < 100 ? 5 : 8,
        showTickLabels: size >= 96,
        colorZones: [
            { from: 0, to: 0.4, color: '#3b82f6' },
            { from: 0.4, to: 0.75, color: theme.ok },
            { from: 0.75, to: 1, color: theme.warning },
        ],
    });
    setParts(group, { needle: parts.needle, label: parts.valueText, inner: inner });
    setRefresh(group, function (v) {
        updateDialNeedle(parts.needle, cx, v, max, r, undefined, undefined, undefined, parts.valueArc);
        parts.valueText.text = "".concat(Math.round(v));
    });
    setState(group, { width: bounds.width, height: bounds.height, size: size, value: value, max: max, display: 'analog' });
    return group;
});
function voltageFillLevel(value) {
    return clamp((value - 11.0) / (14.2 - 11.0), 0, 1);
}
registerAutomotive('batteryVoltage', function (props, app) {
    var value = num(props, 'value', 12.4);
    var lowThreshold = num(props, 'lowThreshold', 11.5);
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 100, 36);
    var group = createAutoGroup(app, 'batteryVoltage', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'batteryVoltage');
    var pad = bounds.pad;
    var panelW = bounds.innerWidth;
    var panelH = bounds.innerHeight;
    var compact = panelW < 56 || panelH < 22;
    var low = value < lowThreshold;
    var levelColor = low ? theme.warning : theme.ok;
    group.add(app.roundedRect({
        x: pad,
        y: pad,
        width: panelW,
        height: panelH,
        cornerRadius: Math.min(6, panelH * 0.2),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var bodyW = Math.max(compact ? 10 : 18, Math.min(panelW * 0.28, panelH * 0.65));
    var bodyH = Math.max(compact ? 7 : 11, Math.min(panelH * 0.5, bodyW * 0.48));
    var nubW = Math.max(2, bodyW * 0.12);
    var iconX = pad + Math.max(compact ? 3 : 6, panelW * 0.05);
    var iconY = pad + (panelH - bodyH) / 2;
    var inset = Math.max(1.5, bodyW * 0.1);
    var innerFillW = Math.max(0, bodyW - inset * 2);
    group.add(app.roundedRect({
        x: iconX,
        y: iconY,
        width: bodyW,
        height: bodyH,
        cornerRadius: Math.min(2, bodyH * 0.2),
        fill: null,
        stroke: theme.textMuted,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var fill = app.roundedRect({
        x: iconX + inset,
        y: iconY + inset,
        width: innerFillW * voltageFillLevel(value),
        height: bodyH - inset * 2,
        cornerRadius: 1,
        fill: levelColor,
        listening: false,
    });
    group.add(fill, app.rect({
        x: iconX + bodyW,
        y: iconY + bodyH * 0.22,
        width: nubW,
        height: bodyH * 0.56,
        fill: theme.textMuted,
        listening: false,
    }));
    var textGap = Math.max(3, panelW * 0.04);
    var textX0 = iconX + bodyW + nubW + textGap;
    var textW = Math.max(18, pad + panelW + pad - textX0 - (compact ? 2 : 4));
    var val = "".concat(value.toFixed(1), "V");
    var maxFs = fluidFont(14, bounds, compact ? 8 : 10, 16);
    var fitted = fitFontSizeToWidth(val, textW, maxFs, compact ? 7 : 8);
    var label = app.text({
        text: val,
        x: textX0 + fitted.x,
        y: pad + textYForBaseline(panelH / 2, fitted.fontSize),
        fontSize: fitted.fontSize,
        fontWeight: 'bold',
        fill: levelColor,
        textAlign: 'left',
        listening: false,
    });
    group.add(label);
    setParts(group, { label: label, fill: fill });
    setRefresh(group, function (v) {
        var isLow = v < lowThreshold;
        var color = isLow ? theme.warning : theme.ok;
        var next = "".concat(v.toFixed(1), "V");
        var nextFit = fitFontSizeToWidth(next, textW, maxFs, compact ? 7 : 8);
        label.text = next;
        label.fontSize = nextFit.fontSize;
        label.x = textX0 + nextFit.x;
        label.y = pad + textYForBaseline(panelH / 2, nextFit.fontSize);
        label.fill = color;
        fill.width = innerFillW * voltageFillLevel(v);
        fill.fill = color;
    });
    setState(group, { value: value, lowThreshold: lowThreshold, width: bounds.width, height: bounds.height });
    return group;
});
registerAutomotive('fuelGauge', function (props, app) {
    var value = clamp(num(props, 'value', 50), 0, 100);
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 120, 56);
    var group = createAutoGroup(app, 'fuelGauge', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'fuelGauge');
    var w = bounds.innerWidth;
    var h = bounds.innerHeight;
    var trackH = Math.max(6, Math.round(h * 0.14));
    var trackY = h - trackH - 8;
    var trackW = w - 16;
    group.add(app.roundedRect({ width: w, height: h, cornerRadius: 8, fill: '#111827', stroke: theme.dialStroke, strokeWidth: 1, listening: false }), app.text({ text: 'FUEL', fontSize: fluidFont(9, bounds, 7, 11), fontWeight: '600', fill: theme.textMuted, x: 8, y: 6, listening: false }));
    var fill = app.roundedRect({
        x: 8,
        y: trackY,
        width: (trackW * value) / 100,
        height: trackH,
        fill: value < 15 ? theme.warning : theme.ok,
        cornerRadius: trackH / 2,
        listening: false,
    });
    var label = autoCenteredText(app, "".concat(value, "%"), w, h * 0.4, {
        fontSize: fluidFont(14, bounds, 10, 16),
        fontWeight: 'bold',
        fill: theme.text,
    });
    group.add(fill, label);
    setParts(group, { fill: fill, label: label });
    setRefresh(group, function (v) {
        var lv = clamp(v, 0, 100);
        fill.width = (trackW * lv) / 100;
        fill.fill = lv < 15 ? theme.warning : theme.ok;
        label.text = "".concat(Math.round(lv), "%");
    });
    setState(group, { value: value, width: bounds.width, height: bounds.height });
    return group;
});
